/**
 * P19 §3's one call — `POST /privacy/erasure`, wrapped over `useSession().request()`, the one
 * authenticated door (`web/src/auth/context.ts`).
 *
 * **This module implements no erasure.** P10 owns the tombstones, the pepper invariant, and the
 * restore-replay tool; P19 adds a door and nothing else (P19 §3: *"P19 adds a door, not a second
 * implementation"*). It lives here rather than in `screens/shared/api.ts` for the same reason
 * `screens/devices/api.ts` does: that file is P7's, and this PRD extends P6's sidebar without
 * taking P7's files.
 */

import { buildPath, ROUTES } from '@selogate/core'
import type { ApiRequest, ApiResponse } from '../auth/transport'
import { field, stringField } from '../auth/transport'

export type ApiRequestFn = (request: ApiRequest) => Promise<ApiResponse>

export type ErasureResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly status: number; readonly code?: string }

/**
 * The literal the route's body schema demands — `packages/functions/privacy/schemas.ts`'s
 * `ERASURE_CONFIRMATION`.
 *
 * **It is written twice, and that is the accepted cost.** `web` depends on `@selogate/core`
 * alone, and the constant lives in a `packages/functions` module the browser bundle cannot
 * import; promoting it to `core` would be an edit to P10's file for a value P10 chose. The
 * failure mode if the two ever drift is loud rather than silent — the schema answers `422`
 * naming the field, on the one tap where a resident is watching — which is the opposite of the
 * `BrokerCode` case (D131) where the two spellings both "worked" and nothing was ever heard.
 *
 * **It is not the confirmation the resident types.** That one is the account's own phone number,
 * sent beside this as `confirmPhone` and compared against the profile server-side; this is the
 * API's own guard against an empty-bodied `POST` (P10's schema docblock), and a client that sent
 * it on a stray retry would still have had to pass the typed number and the slider first.
 */
export const ERASURE_CONFIRMATION = 'erase-my-account'

/** What `202` carries back (`packages/functions/privacy/routes.ts`'s `ErasureResponse`). */
export interface ErasureAccepted {
  readonly erasedAt?: string
  /** Gates whose last manager was the erased account. Empty in the ordinary case. */
  readonly gatesWithoutManager: readonly string[]
}

/**
 * `POST /privacy/erasure` — erases the **caller's own** account. There is no subject parameter,
 * and there is no undo.
 *
 * **`202` is the success, not `200`.** The destructive pass finishes inside the request; the log
 * sweeps do not and cannot, so the answer means *the account is gone and the history is being
 * pseudonymized*. A caller must not wait for anything further.
 *
 * A `404` is **also** a terminal success from the client's point of view — a session whose
 * profile is already gone has nothing left to erase — and the caller treats it that way rather
 * than showing a retry for work that can never succeed.
 *
 * `confirmPhone` is the number the resident typed, **sent as typed**. Canonicalization is the
 * schema's (`normalizePhone`, one implementation), so the client cannot be the place where two
 * spellings of one number stop agreeing — and a number that is not this account's comes back
 * `403 phone-mismatch` rather than erasing anything.
 */
export async function eraseMyAccount(
  request: ApiRequestFn,
  confirmPhone: string,
): Promise<ErasureResult<ErasureAccepted>> {
  const response = await request({
    method: ROUTES.privacyErasure.method,
    path: buildPath(ROUTES.privacyErasure),
    body: { confirm: ERASURE_CONFIRMATION, confirmPhone },
  })
  if (response.status < 200 || response.status >= 300) {
    const code = stringField(response.body, 'code')
    return { ok: false, status: response.status, ...(code === undefined ? {} : { code }) }
  }
  const erasedAt = stringField(response.body, 'erasedAt')
  const raw = field(response.body, 'gatesWithoutManager')
  return {
    ok: true,
    data: {
      ...(erasedAt === undefined ? {} : { erasedAt }),
      gatesWithoutManager: Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [],
    },
  }
}
